import { Map } from 'immutable';
import memoize from 'lodash/memoize';
import { createSelector } from 'reselect';

import { musicIdPrefixToType, THUMBPRINT_RADIO_MUSIC_ID } from '../constants/Music';

import { getArtistEntities } from './EntityUtils';

/**
 * Utility function to check if artist object is loaded with info fetched from
 * a call to music/artist instead of partially from other sources.
 *
 * @param {Map} artist - the artist
 * @returns {boolean} - true if artist has all the info, false otherwise
 */
export function artistHasFullInfo(artist) {
  if (!artist) {
    return false;
  }
  const baseFields = ['seoToken', 'detailUrl', 'similar'];
  let isFullInfo = true;
  baseFields.forEach(field => {
    if (!artist.has(field)) {
      isFullInfo = false;
    }
  });
  return isFullInfo;
}

/**
 * Utility function to check if track object is loaded with info fetched from
 * a call to music/track instead of partially from other sources.
 *
 * @param {Map} track - the track
 * @returns {boolean} - true if track has all the info, false otherwise
 */
export function trackHasFullInfo(track) {
  if (!track) {
    return false;
  }
  const baseFields = ['trackDetailUrl', 'similar'];
  let isFullInfo = true;
  baseFields.forEach(field => {
    if (!track.has(field)) {
      isFullInfo = false;
    }
  });
  return isFullInfo;
}

/**
 * Utility function to check if track object is loaded with info fetched from
 * a call to music/track instead of partially from other sources.
 *
 * @param {Map} album - the album
 * @returns {boolean} - true if track has all the info, false otherwise
 */
export function albumHasFullInfo(album) {
  if (!album) {
    return false;
  }
  const baseFields = ['amazonUrl', 'iTunesUrl', 'seoToken', 'artistName', 'albumTitle', 'art'];
  let isFullInfo = true;
  baseFields.forEach(field => {
    if (!album.has(field)) {
      isFullInfo = false;
    }
  });
  return isFullInfo;
}

/**
 * Utility function to check if track object is loaded with info fetched from
 * a call to music/track instead of partially from other sources.
 *
 * @param {Map} station - the station
 * @returns {boolean} - true if track has all the info, false otherwise
 */
export function stationHasFullInfo(station) {
  if (!station) {
    return false;
  }
  const baseFields = ['stationId', 'name', 'art', 'dateCreated', 'initialSeed'];
  baseFields.forEach(field => {
    if (!station.has(field)) {
      return false;
    }
  });
  return true;
}

/**
 * @param  {object} artistEntities - artist entities the redux store state
 * @param  {string} musicId - the unique id of the artist
 * @returns {Map} - an immutable Map of the artist loaded into the app
 */
export function getArtistFromState(artistEntities, musicId) {
  return artistEntities.get(musicId) || null;
}

/**
 * @param {object} artistEntities -
 * @returns {Function} -
 * @private
 */
export function _memoizeGetArtistById(artistEntities) {
  return memoize(musicId => getArtistFromState(artistEntities, musicId));
}

/**
 * @param  {object} artistEntities - artist entities the redux store state
 * @param  {string} seoToken - the unique seoToken of the artist
 * @returns {Map} - an immutable Map of the artists loaded into the app
 */
export function getArtistFromStateByToken(artistEntities, seoToken) {
  return (
    artistEntities.find(artist => {
      const tokens = artist.get('otherArtistSeoTokens');
      return artist.get('seoToken') === seoToken || (tokens && tokens.includes(seoToken));
    }) || null
  );
}

export const createGetArtistByMusicIdSelector = createSelector(
  getArtistEntities,
  _memoizeGetArtistById,
);

/**
 * @param  {object} albumEntities - albums entities from the redux store state
 * @param  {string} seoToken - the unique seoToken of the album
 * @returns {Map} - albums
 */
export function getAlbumFromStateByToken(albumEntities, seoToken) {
  return (
    albumEntities.find(album => {
      const tokens = album.get('otherAlbumSeoTokens');
      return album.get('seoToken') === seoToken || (tokens && tokens.includes(seoToken));
    }) || null
  );
}

/**
 * @param  {object} albumEntities - albums entities from the redux store state
 * @param  {string} musicId - the unique id of the track
 * @returns {List} - Albums
 */
export function getAlbumFromStateByMusicId(albumEntities, musicId) {
  return albumEntities.get(musicId);
}

/**
 * @param  {object} albumEntities - albums entities from the redux store state
 * @param  {string} pandoraId - the unique id of the track
 * @returns {object} - Album or undefined
 */
export function getAlbumFromStateByPandoraId(albumEntities, pandoraId) {
  return albumEntities.find(album => {
    return album.get('pandoraId') === pandoraId;
  });
}

/**
 * @param  {object} state - the redux store state
 * @param  {string} musicId - the unique id of the track
 * @returns {List} - Track
 */
export function getTrackFromState(state, musicId) {
  return state.entities.getIn(['tracks', musicId], null);
}

/**
 * @param  {object} state - the redux store state
 * @param  {string} pandoraId - the unique id of the playlist
 * @returns {List} - Playlist
 */
export function getPlaylistFromState(state, pandoraId) {
  return state.entities.getIn(['playlists', pandoraId], null);
}

export function getPlaylistsFromState(state, pandoraIds) {
  return pandoraIds.map(playlistId => {
    return state.entities.getIn(['playlists', playlistId], null);
  });
}

export function getTrackObjectsForPlaylist(state, pandoraId) {
  return state.entities.getIn(['playlists', pandoraId, 'tracks'], null);
}

export function getPlaylistTotalTracks(state, pandoraId) {
  return state.entities.getIn(['playlists', pandoraId, 'totalTracks'], 0);
}

/**
 * @param  {object} state - the redux store state
 * @param  {string} stationId - the id of the station to search for
 * @returns {List} - Playlist if there is an automatic playlist linked to the station
 */
export function findLinkedPlaylist(state, stationId) {
  let result = null;
  const playlists = state.entities.get('playlists');
  if (playlists) {
    result = playlists.find(playlist => playlist && playlist.get('linkedSourceId') === stationId);
  }
  return result;
}

export function findTrackIndexInPlaylist(playlist, trackId) {
  let result = -1;
  const tracks = playlist.get('tracks', null);
  if (tracks) {
    result = tracks.findIndex(value => value && value.get('trackPandoraId') === trackId);
  }
  return result;
}

export function findMyThumbsUpPlaylist(state) {
  let result = null;
  const playlists = state.entities.get('playlists');
  if (playlists) {
    result = playlists.find(playlist => playlist && playlist.get('linkedType') === 'MyThumbsUp');
  }
  return result;
}

/**
 * @param  {object} state - the redux store state
 * @param  {seoToken} seoToken - the seo Token for track
 * @returns {null|List} - Track
 */
export function getTrackFromStateBySeoToken(state, seoToken) {
  const tracks = state.entities.get('tracks');
  const thisTrack =
    tracks.find(track => {
      const tokens = track.get('otherTrackSeoTokens');
      return track.get('trackSeoToken') === seoToken || (tokens && tokens.includes(seoToken));
    }) || null;
  if (trackHasFullInfo(thisTrack)) {
    return thisTrack;
  }
  return null;
}

/**
 * @param  {object} state - the redux store state
 * @param  {string} musicId - the unique musicId of the artist
 * @returns {Map} - an immutable Map of the artist loaded into the app
 */
export function getArtistByMusicId(state, musicId) {
  return state.entities.get('artists').get(musicId);
}

/**
 * @param {object} artistEntities -
 * @returns {Function} -
 * @private
 */
export function _memoizeGetArtistByToken(artistEntities) {
  return memoize(token => getArtistFromStateByToken(artistEntities, token));
}

/**
 * Use case: you want to get an artist by seo token in an "efficient" way, most likely from within a component's `mapStateToProps` method.
 *
 * @param {Function} - function to get artist entities from state
 * @returns {Function} - A memoized function which takes an SEO token and returns an artist
 */
export const createGetArtistByTokenSelector = createSelector(
  getArtistEntities,
  _memoizeGetArtistByToken,
);

/**
 *@param  {object} albumEntities - albums entities from the redux store state
 * @returns {Function} -
 * @private
 */
export function _memoizeGetAlbumByToken(albumEntities) {
  return memoize(token => getAlbumFromStateByToken(albumEntities, token));
}

/**
 * @param  {object} albumEntities - albums entities from the redux store state
 * @returns {Function} -
 * @private
 */
export function _memoizeGetAlbumByMusicId(albumEntities) {
  return memoize(musicId => getAlbumFromStateByMusicId(albumEntities, musicId));
}

/**
 * @param  {object} albumEntities - albums entities from the redux store state
 * @returns {Function} -
 * @private
 */
export function _memoizeGetAlbumByPandoraId(albumEntities) {
  return memoize(pandoraId => getAlbumFromStateByPandoraId(albumEntities, pandoraId));
}

export function getAlbumEntities(state) {
  return state.entities.get('albums');
}

/**
 * Use case: you want to get an album by token in an "efficient" way, most likely from within a component's `mapStateToProps` method.
 *
 * @param {object} state
 * @returns {Function} - A memoized function which takes an SEO token and returns an album
 */
export const createGetAlbumByTokenSelector = createSelector(
  getAlbumEntities,
  _memoizeGetAlbumByToken,
);

/**
 * Use case: you want to get an album by token in an "efficient" way, most likely from within a component's `mapStateToProps` method.
 *
 * @param {object} state
 * @returns {Function} - A memoized function which takes a music Id and returns an album
 */
export const createGetAlbumByMusicIdSelector = createSelector(
  getAlbumEntities,
  _memoizeGetAlbumByMusicId,
);

export const createGetMAPIAlbumByPandoraIdSelector = createSelector(
  getAlbumEntities,
  _memoizeGetAlbumByPandoraId,
);

// GET GENRE
/**
 * Use case: tell the difference between a genreId (e.g. 'G100') and a genre token (e.g. 'todays-hits')
 *
 * @param {string} string - id to check
 * @returns {boolean} - true if string was a musicID, false if not (usually indicating it was a seoToken)
 */
export function isGenreMusicId(string) {
  return /^G[0-9]+$/.test(string);
}

export function getGenreByMusicId(state, musicId) {
  return state.entities.get('genres').get(musicId);
}

export function getGenreByToken(state, token) {
  return state.entities.get('genres').find(genre => {
    return genre.get('token') === token;
  });
}

export function getGenreByPandoraId(state, pandoraId) {
  return state.entities.get('genres').find(genre => genre.get('pandoraId') === pandoraId);
}

/**
 * @param  {object} state - the redux store state
 * @param  {string} identifier - a musicId or seoToken representing the genre
 * @returns {Map} - an immutable Map containing the genre entity
 */
export function getGenre(state, identifier) {
  if (isGenreMusicId(identifier)) {
    return getGenreByMusicId(state, identifier);
  }
  return getGenreByToken(state, identifier);
}

/**
 * @param  {object} state - the redux store state
 * @param  {string} pandoraId - the unique id of the track the lyrics belong to
 * @returns {Map} - an immutable Map of the lyrics
 */
export function getFullLyricsFromState(state, pandoraId) {
  return state.entities.get('lyrics').get(pandoraId);
}

function isThumbprintRadio(musicId) {
  return musicId === THUMBPRINT_RADIO_MUSIC_ID;
}

export function getTypeCodeFromMusicId(musicId) {
  if (isThumbprintRadio(musicId)) {
    return 'P';
  }

  return musicId.substr(0, 1);
}

export function isOnDemandIdLike(id) {
  const PANDORAID_REGEX = new RegExp('^[A-Z]{2,}(:)+[d]*');
  return PANDORAID_REGEX.test(id);
}

export function getNewTracksFromEdits(moves, tracks) {
  if (moves && tracks) {
    const moveMap = moves.reduce((accum, move) => {
      accum[move.newIndex] = tracks.get(move.oldIndex);
      return accum;
    }, {});
    return tracks.map((track, i) => {
      if (moveMap[i]) return moveMap[i];
      return track;
    });
  }
  return tracks;
}

export function getValidInserts(inserts, tracks) {
  if (inserts && tracks) {
    const newTracks = inserts.filter((insert, idx) => {
      return insert.newIndex - idx === tracks.size;
    });
    return newTracks;
  }
  return [];
}

/**
 *
 * @param {string} musicId Id from which type can be derived
 * @param {string} normalize If true, Composers are considered Artists
 * @returns {string} Music type
 */
export function getTypeFromMusicId(musicId, normalize) {
  const typeCode = getTypeCodeFromMusicId(musicId);
  const type = musicIdPrefixToType[typeCode];
  return normalize && type === 'composer' ? 'artist' : type;
}

export function getAlbumByPandoraId(state, pandoraId) {
  const selector = createGetMAPIAlbumByPandoraIdSelector(state);
  return selector(pandoraId) || Map();
}
